/** Metadatos pedagógicos derivados de MusicXML para la biblioteca. */

export type SongFormatTag = "GUITAR" | "PIANO" | "ENSAMBLE" | "PARTITURA";

export type SongDifficulty = 1 | 2 | 3;

export interface SongLibraryMeta {
  tonality: string;
  tonalityMode: "major" | "minor";
  format: SongFormatTag;
  difficulty: SongDifficulty;
}

const SHARP_ROOTS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
const FLAT_ROOTS = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"] as const;

function keyFromXml(xml: string): { label: string; mode: "major" | "minor" } {
  const keyBlock = xml.match(/<key>[\s\S]*?<\/key>/);
  const fifthsMatch = keyBlock?.[0].match(/<fifths>(-?\d+)<\/fifths>/);
  const modeMatch = keyBlock?.[0].match(/<mode>(\w+)<\/mode>/);
  const fifths = fifthsMatch ? parseInt(fifthsMatch[1], 10) : 0;
  const rawMode = (modeMatch?.[1] ?? "major").toLowerCase();
  const isMinor = rawMode === "minor" || rawMode === "aeolian" || rawMode === "dorian";
  const majorRoot = ((fifths * 7) % 12 + 12) % 12;
  const tonic = isMinor ? (majorRoot - 3 + 12) % 12 : majorRoot;
  const roots = fifths < 0 ? FLAT_ROOTS : SHARP_ROOTS;
  const root = roots[tonic] ?? "C";
  return {
    label: isMinor ? `${root}m` : root,
    mode: isMinor ? "minor" : "major",
  };
}

function formatFromXml(xml: string): SongFormatTag {
  const scoreParts = xml.match(/<score-part\b/g)?.length ?? 0;
  const parts = xml.match(/<part\b/g)?.length ?? 0;
  const partCount = Math.max(scoreParts, parts);
  if (partCount > 2) return "ENSAMBLE";
  if (/<technical>[\s\S]*?<string>/i.test(xml)) return "GUITAR";
  const hasBassClef = /<sign>\s*F\s*<\/sign>/i.test(xml);
  const hasGrandStaff = /<staff>\s*2\s*<\/staff>/i.test(xml);
  if (hasBassClef || hasGrandStaff) return "PIANO";
  return "PARTITURA";
}

function difficultyFromXml(xml: string): SongDifficulty {
  const measures = xml.match(/<measure\s/g)?.length ?? 0;
  const notes = xml.match(/<note[\s>]/g)?.length ?? 0;
  const highFrets = xml.match(/<fret>(1[2-9]|[2-9]\d)</g)?.length ?? 0;
  let score = 0;
  if (measures > 24) score += 1;
  if (measures > 48) score += 1;
  if (notes > 180) score += 1;
  if (notes > 400) score += 1;
  if (highFrets > 8) score += 1;
  if (score <= 1) return 1;
  if (score <= 3) return 2;
  return 3;
}

export function extractSongLibraryMeta(musicXml?: string): SongLibraryMeta {
  if (!musicXml?.trim()) {
    return {
      tonality: "—",
      tonalityMode: "major",
      format: "PARTITURA",
      difficulty: 1,
    };
  }
  const { label, mode } = keyFromXml(musicXml);
  return {
    tonality: label,
    tonalityMode: mode,
    format: formatFromXml(musicXml),
    difficulty: difficultyFromXml(musicXml),
  };
}

export type SongSortKey =
  | "recent"
  | "title-asc"
  | "title-desc"
  | "difficulty-asc"
  | "difficulty-desc"
  | "tonality-asc";

export const SONG_SORT_OPTIONS: { value: SongSortKey; label: string }[] = [
  { value: "recent", label: "Recientes" },
  { value: "title-asc", label: "Título A → Z" },
  { value: "title-desc", label: "Título Z → A" },
  { value: "difficulty-asc", label: "Dificultad ↑" },
  { value: "difficulty-desc", label: "Dificultad ↓" },
  { value: "tonality-asc", label: "Tonalidad" },
];

export function compareSongs(
  a: { title: string; createdAt?: { toMillis?: () => number } | Date; meta: SongLibraryMeta },
  b: { title: string; createdAt?: { toMillis?: () => number } | Date; meta: SongLibraryMeta },
  sort: SongSortKey
): number {
  switch (sort) {
    case "title-asc":
      return a.title.localeCompare(b.title, "es");
    case "title-desc":
      return b.title.localeCompare(a.title, "es");
    case "difficulty-asc":
      return a.meta.difficulty - b.meta.difficulty;
    case "difficulty-desc":
      return b.meta.difficulty - a.meta.difficulty;
    case "tonality-asc":
      return a.meta.tonality.localeCompare(b.meta.tonality, "es");
    case "recent":
    default: {
      const ta =
        typeof a.createdAt === "object" && a.createdAt && "toMillis" in a.createdAt
          ? a.createdAt.toMillis?.() ?? 0
          : a.createdAt instanceof Date
            ? a.createdAt.getTime()
            : 0;
      const tb =
        typeof b.createdAt === "object" && b.createdAt && "toMillis" in b.createdAt
          ? b.createdAt.toMillis?.() ?? 0
          : b.createdAt instanceof Date
            ? b.createdAt.getTime()
            : 0;
      return tb - ta;
    }
  }
}
